import { useEffect, useRef, useState } from "react";

/** How long a finished state is allowed to linger before Dany settles. */
export const SETTLE_MS = 3000;

/**
 * Dany's face never animates indefinitely after a reply (Jorge's DY1.4 spec,
 * point 5).
 *
 * Pass the LIVE state while something is happening — `thinking`, `working`,
 * `speaking`, `listening` — and `null` the moment the turn ends, whether it
 * ended with `done` or with `error`. The hook holds the closing state for at
 * most `SETTLE_MS` and then returns `idle`.
 *
 * Passing `null` and reading `idle` immediately was the other option and it is
 * worse: the result state (`happy`, or `error`) is the only frame that tells the
 * client the turn actually landed, and dropping it makes a successful answer and
 * an abandoned one look identical.
 */
export function useDanyAvatarState(live, { settleAs = "idle", holdMs = SETTLE_MS } = {}) {
  const [state, setState] = useState(live || "idle");
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (live) {
      setState(live);
      return undefined;
    }
    // The turn is over: keep whatever was last on screen, then settle.
    timer.current = setTimeout(() => setState(settleAs), holdMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [live, settleAs, holdMs]);

  return state;
}

export default useDanyAvatarState;
